import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { InvestmentRoundingSetting, roundInvestmentShares } from 'shared';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useFormat } from '../../../../hooks/index';
import { EventFormType, EventTypeButtonTitle } from '../../../../types/events.types';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { toNumber } from '../../../../utils/to-number.util';
import {
  getTemplateSignatoryBaseType,
  getTemplateSignatoryLabelKey,
  GRANTEE,
  templateSignatoryTypeTranslation,
} from '../../../documents/templates/constants/template-signatory-type.constant';
import { useTemplateById } from '../../../documents/templates/hooks/use-template-by-id.hook';
import { EventAggregateDocumentsPreview } from '../components/event-aggregate-documents-preview';
import { EventTransactionAttachedFilesPreview } from '../components/event-transaction-attached-files-preview';
import {
  GrantSummaryPlainFields,
  GrantSummaryPlainRow,
  GrantSummarySection,
  joinGranteeNamesForDisplay,
} from '../components/grant-event-summary-layout';
import { ReverseVestingSummaryRows } from '../components/reverse-vesting-summary-rows';
import { ChildrenFormProps } from '../event-form.types';
import {
  mergeGrantSignatoryAssignmentsForDisplay,
  resolveGrantSignatoryDisplayName,
} from '../utils/grant-signatory-display';

export type FinalStepProps = ChildrenFormProps;

export const FinalStep: FC<FinalStepProps> = ({
  handleCloseModal,
  nextFormStep,
  event,
  formData,
  prevFormStep,
  issuedShares,
  isLoading,
}) => {
  const { initialStep, stepOne, optionalStep, stepTwo } = formData();
  const { format } = useFormat();

  const [clicked, setClicked] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoading) {
      setClicked(false);
    }
  }, [isLoading]);

  const handleClick = () => {
    if (!clicked) {
      setClicked(true);
      nextFormStep();
    }
  };

  const calculateShares = useCallback(
    (index: number) => {
      const investment = toNumber(
        optionalStep?.['fundraising-round']?.investmentItems?.[index]?.investment,
      );
      const sharePrice = toNumber(stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice);

      const roundingSetting =
        stepOne?.eventDetails?.['fundraising-round']?.roundingSetting ??
        InvestmentRoundingSetting.DEFAULT;

      if (investment && sharePrice) {
        return roundInvestmentShares(investment, sharePrice, roundingSetting);
      }

      return 0;
    },
    [optionalStep, stepOne?.eventDetails],
  );

  const grantData = stepOne.eventDetails?.grant;
  const {
    enabled,
    grantItems = [],
    selectedTemplateId,
    sharePlan,
    signatoryAssignments: formSignatoryAssignments = {},
  } = grantData ?? {};

  const signatoryAssignmentsForDisplay = useMemo(
    () => mergeGrantSignatoryAssignmentsForDisplay(formSignatoryAssignments, event),
    [formSignatoryAssignments, event],
  );

  const isRenderGrant =
    initialStep?.type === EventFormType.GRANT && enabled && grantItems.length > 0;

  const { template: grantTemplate } = useTemplateById(selectedTemplateId ?? null);

  const isGrantWithSignature = Boolean(
    isRenderGrant && selectedTemplateId && grantTemplate?.signatoryTypes?.length,
  );

  const grantRecipientsSummary = useMemo(() => {
    if (!isGrantWithSignature || !grantTemplate) return null;
    const orderedTypes = [...grantTemplate.signatoryTypes].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
    const signatoryLabelCounts: Record<string, number> = {};
    orderedTypes.forEach((st) => {
      const labelKey =
        getTemplateSignatoryLabelKey(st.stakeholderType) ??
        templateSignatoryTypeTranslation[GRANTEE];
      signatoryLabelCounts[labelKey] = (signatoryLabelCounts[labelKey] ?? 0) + 1;
    });
    const signatoryLabelSeen: Record<string, number> = {};
    return orderedTypes.map((st) => {
      const baseSignatoryType = getTemplateSignatoryBaseType(st.stakeholderType);
      const labelKey =
        getTemplateSignatoryLabelKey(st.stakeholderType) ??
        templateSignatoryTypeTranslation[GRANTEE];
      signatoryLabelSeen[labelKey] = (signatoryLabelSeen[labelKey] ?? 0) + 1;
      const shouldShowIndex = signatoryLabelCounts[labelKey] > 1;
      const label = (
        <>
          <AppFormattedMessage id={labelKey} />
          {shouldShowIndex ? ` ${signatoryLabelSeen[labelKey]}` : ''}
        </>
      );
      const granteeNames = grantItems.map((item) => item.stakeholder.fullName);
      const value =
        baseSignatoryType === GRANTEE
          ? joinGranteeNamesForDisplay(granteeNames)
          : resolveGrantSignatoryDisplayName(st.id, signatoryAssignmentsForDisplay, grantItems);
      const isGranteeRow = baseSignatoryType === GRANTEE;
      return { isGranteeRow, label, value };
    });
  }, [isGrantWithSignature, grantTemplate, grantItems, signatoryAssignmentsForDisplay]);

  const isRenderFundraising =
    initialStep?.type === EventFormType.FUNDRAISING_ROUND &&
    stepOne.eventDetails?.['fundraising-round']?.enabled &&
    optionalStep?.['fundraising-round']?.investmentItems;

  const isRenderShareIssuance =
    initialStep?.type === EventFormType.SHARE_ISSUANCE &&
    stepOne.eventDetails?.['share-issuance']?.enabled &&
    optionalStep?.['share-issuance']?.issuanceItems;

  const isRenderBuyBack =
    initialStep?.type === EventFormType.BUYBACK &&
    stepOne.eventDetails?.buyback?.enabled &&
    stepOne.eventDetails?.buyback?.buyBackItems;

  const isRenderSecondaries =
    initialStep?.type === EventFormType.SECONDARIES &&
    stepOne.eventDetails?.secondaries?.enabled &&
    stepOne.eventDetails?.secondaries?.secondariesItems;

  const isRenderClassConversion =
    initialStep?.type === EventFormType.CONVERSION &&
    stepOne.eventDetails?.['conversion']?.enabled &&
    stepOne.eventDetails?.['conversion']?.classConversionItems;

  const isRenderValuation =
    initialStep?.type === EventFormType.VALUATION && stepOne.eventDetails?.valuation?.enabled;

  const roundedSharePrice = toMathRound(
    stepOne.eventDetails?.valuation?.sharePrice ||
      stepOne.eventDetails['share-issuance']?.event?.sharePrice ||
      stepOne.eventDetails['fundraising-round']?.event.sharePrice,
  );

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex w-full shrink-0 flex-col gap-4">
          {isRenderGrant && (
            <div className="flex h-fit flex-col gap-4">
              <GrantSummarySection title={<AppFormattedMessage id="GENERAL" />}>
                <SummaryItem
                  className="border-none py-0 pb-1 pt-1"
                  title={<AppFormattedMessage id="PLAN_NAME" />}
                  titleClassName="w-fit"
                  titleWrapperClassName="w-[120px]"
                  value={
                    <Tooltip content={sharePlan?.name ?? ''}>{sharePlan?.name ?? '—'}</Tooltip>
                  }
                />
              </GrantSummarySection>

              {grantItems.map((item, index) => (
                <GrantSummarySection
                  key={index}
                  title={
                    <Tooltip
                      childrenWrapperClassName="max-w-[380px]"
                      content={item.stakeholder.fullName}
                    >
                      <span className="truncate">
                        {<AppFormattedMessage id="GRANT" />} - {item.stakeholder.fullName}
                      </span>
                    </Tooltip>
                  }
                >
                  <SummaryItem
                    className="border-b-[1px] border-gray-100 pt-0"
                    title={<AppFormattedMessage id="DATE" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={item.grantDate ? format(item.grantDate, 'dd/MM/yyyy') : '-'}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="STAKEHOLDER" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={
                      <Tooltip content={item.stakeholder.fullName}>
                        {item.stakeholder.fullName}
                      </Tooltip>
                    }
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="NO_OF_SHARES" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={Number(item.shares).toLocaleString('en-US') || ''}
                  />
                  <EventTransactionAttachedFilesPreview
                    event={event}
                    files={item.files}
                    transactionId={item.id}
                  />
                </GrantSummarySection>
              ))}

              {grantRecipientsSummary && grantRecipientsSummary.length > 0 && (
                <GrantSummarySection title={<AppFormattedMessage id="RECIPIENTS" />}>
                  {grantRecipientsSummary.map(({ label, value }, i) => (
                    <SummaryItem
                      className={twMerge(
                        i < grantRecipientsSummary.length - 1 && 'border-b-[1px] border-gray-100',
                        i === 0 && 'pt-0',
                        i === grantRecipientsSummary.length - 1 && 'pb-0',
                      )}
                      key={i}
                      title={label}
                      titleClassName="w-fit"
                      titleWrapperClassName="w-[120px]"
                      value={value}
                    />
                  ))}
                </GrantSummarySection>
              )}
            </div>
          )}

          {isRenderFundraising && (
            <div className="flex h-fit flex-col gap-4">
              <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                <span className="px-4 text-sm font-[550] text-gray-700">
                  <AppFormattedMessage id="GENERAL" />
                </span>
                <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                  <div className="flex w-full gap-3 border-b-[1px] border-gray-100 py-3">
                    <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
                      <AppFormattedMessage id="ROUND_STATUS" />:
                    </span>
                    <span
                      className={twMerge(
                        'rounded-2xl px-2 py-[2px] text-xs font-medium',
                        stepOne.eventDetails['fundraising-round']?.isOpen
                          ? 'bg-forest-50 text-forest-600'
                          : 'bg-fireside-50 text-fireside-600',
                      )}
                    >
                      {stepOne.eventDetails['fundraising-round']?.isOpen ? (
                        <AppFormattedMessage id="OPEN" />
                      ) : (
                        <AppFormattedMessage id="CLOSED" />
                      )}
                    </span>
                  </div>
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="ROUND_NAME" />}
                    value={stepOne.eventDetails['fundraising-round']?.event.name || ''}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={
                      stepOne.eventDetails['fundraising-round']?.isOpen ? (
                        <AppFormattedMessage id="OPEN_DATE" />
                      ) : (
                        <AppFormattedMessage id="CLOSING_DATE" />
                      )
                    }
                    value={
                      stepOne.eventDetails['fundraising-round']?.date
                        ? format(stepOne.eventDetails['fundraising-round']?.date, 'dd/MM/yyyy')
                        : '-'
                    }
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="SHARES_PRICE" />}
                    value={roundedSharePrice.toLocaleString('en-US')}
                  />
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
                    value={(stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice
                      ? stepOne.eventDetails['fundraising-round'].event.sharePrice * issuedShares
                      : 0
                    ).toLocaleString('en-US')}
                  />
                </div>
              </div>
              {optionalStep?.['fundraising-round']?.investmentItems.map((item, index) => (
                <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2" key={index}>
                  <Tooltip
                    childrenWrapperClassName="max-w-[500px]"
                    content={item.stakeholder.fullName}
                  >
                    <span className="truncate px-4 text-sm font-[550] text-gray-700">
                      <AppFormattedMessage id="INVESTMENT" /> - {item.stakeholder.fullName}
                    </span>
                  </Tooltip>
                  <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                    <SummaryItem
                      className="border-b-[1px] border-gray-100 pt-0"
                      title={<AppFormattedMessage id="STAKEHOLDER" />}
                      value={
                        <Tooltip content={item.stakeholder.fullName}>
                          {item.stakeholder.fullName}
                        </Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="INVESTMENT" />}
                      value={Number(item.investment).toLocaleString('en-US') || ''}
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="SHARE_CLASS" />}
                      value={
                        <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="NO_OF_SHARES" />}
                      value={
                        calculateShares(index)
                          ? calculateShares(index).toLocaleString('en-US')
                          : '-'
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="INVEST_DATE" />}
                      value={item.investmentDate ? format(item.investmentDate, 'dd/MM/yyyy') : '-'}
                    />
                    <EventTransactionAttachedFilesPreview
                      event={event}
                      files={item.files}
                      transactionId={item.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isRenderShareIssuance && (
            <div className="flex h-fit flex-col gap-4">
              <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                <span className="px-4 text-sm font-[550] text-gray-700">
                  <AppFormattedMessage id="GENERAL" />
                </span>
                <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                  <SummaryItem
                    className="border-b-[1px] border-gray-100 pt-0"
                    title={<AppFormattedMessage id="EVENT_NAME" />}
                    value={stepOne.eventDetails['share-issuance']?.event.name || ''}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="DATE" />}
                    value={
                      stepOne.eventDetails['share-issuance']?.date
                        ? format(stepOne.eventDetails['share-issuance']?.date, 'dd/MM/yyyy')
                        : '-'
                    }
                  />
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="SHARES_PRICE" />}
                    value={roundedSharePrice.toLocaleString('en-US')}
                  />
                </div>
              </div>
              {optionalStep?.['share-issuance']?.issuanceItems.map((item, index) => (
                <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2" key={index}>
                  <Tooltip
                    childrenWrapperClassName="max-w-[500px]"
                    content={item.stakeholder.fullName}
                  >
                    <span className="truncate px-4 text-sm font-[550] text-gray-700">
                      <AppFormattedMessage id="ISSUANCE" /> - {item.stakeholder.fullName}
                    </span>
                  </Tooltip>
                  <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                    <SummaryItem
                      className="border-b-[1px] border-gray-100 pt-0"
                      title={<AppFormattedMessage id="STAKEHOLDER" />}
                      value={
                        <Tooltip content={item.stakeholder.fullName}>
                          {item.stakeholder.fullName}
                        </Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="SHARE_CLASS" />}
                      value={
                        <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="NO_OF_SHARES" />}
                      value={toNumber(item.shares)?.toLocaleString('en-US') || ''}
                    />
                    <ReverseVestingSummaryRows hasRowsBelow reverseVesting={item.reverseVesting} />
                    <EventTransactionAttachedFilesPreview
                      event={event}
                      files={item.files}
                      transactionId={item.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isRenderBuyBack && (
            <div className="flex h-fit flex-col gap-4">
              {stepOne?.eventDetails?.buyback?.buyBackItems.map((item, index) => (
                <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2" key={index}>
                  <Tooltip
                    childrenWrapperClassName="max-w-[500px]"
                    content={item.stakeholder.fullName}
                  >
                    <span className="truncate px-4 text-sm font-[550] text-gray-700">
                      <AppFormattedMessage id="STAKEHOLDER" /> - {item.stakeholder.fullName}
                    </span>
                  </Tooltip>
                  <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                    <SummaryItem
                      className="border-b-[1px] border-gray-100 pt-0"
                      title="Event Date"
                      value={item ? format(item.date, 'dd/MM/yyyy') : '-'}
                    />

                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="STAKEHOLDER" />}
                      value={
                        <Tooltip content={item.stakeholder.fullName}>
                          {item.stakeholder.fullName}
                        </Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="SHARE_CLASS" />}
                      value={
                        <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="NO_OF_SHARES" />}
                      value={Number(item.shares).toLocaleString('en-US') || ''}
                    />
                    <EventTransactionAttachedFilesPreview
                      event={event}
                      files={item.files}
                      transactionId={item.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isRenderSecondaries && (
            <div className="flex h-fit flex-col gap-4">
              {stepOne?.eventDetails?.secondaries?.secondariesItems.map((item, index) => (
                <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2" key={index}>
                  <Tooltip
                    childrenWrapperClassName="max-w-[500px]"
                    content={`${item.stakeholderFrom.fullName} to ${item.stakeholderTo.fullName}`}
                  >
                    <span className="truncate px-4 text-sm font-[550] text-gray-700">
                      <AppFormattedMessage id="STAKEHOLDER" /> - {item.stakeholderFrom.fullName} to{' '}
                      {item.stakeholderTo.fullName}
                    </span>
                  </Tooltip>
                  <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                    <SummaryItem
                      className="border-b-[1px] border-gray-100 pt-0"
                      title={<AppFormattedMessage id="EVENT_DATE" />}
                      value={item.date ? format(item.date, 'dd/MM/yyyy') : '-'}
                    />

                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="FROM_STAKEHOLDER" />}
                      value={
                        <Tooltip content={item.stakeholderFrom.fullName}>
                          {item.stakeholderFrom.fullName}
                        </Tooltip>
                      }
                    />

                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="TO_STAKEHOLDER" />}
                      value={
                        <Tooltip content={item.stakeholderTo.fullName}>
                          {item.stakeholderTo.fullName}
                        </Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="SHARE_CLASS" />}
                      value={
                        <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="NO_OF_SHARES" />}
                      value={Number(item.shares).toLocaleString('en-US') || ''}
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="SHARE_VALUE" />}
                      value={Number(item.shareValue).toLocaleString('en-US') || ''}
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="TRANSFER_AMOUNT" />}
                      value={Number(item.shareValue * item.shares).toLocaleString('en-US') || ''}
                    />
                    <EventTransactionAttachedFilesPreview
                      event={event}
                      files={item.files}
                      transactionId={item.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isRenderClassConversion && (
            <div className="flex h-fit flex-col gap-4">
              {stepOne?.eventDetails?.['conversion']?.classConversionItems.map((item, index) => (
                <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2" key={index}>
                  <Tooltip
                    childrenWrapperClassName="max-w-[500px]"
                    content={item.stakeholder.fullName}
                  >
                    <span className="truncate px-4 text-sm font-[550] text-gray-700">
                      Conversion - {item.stakeholder.fullName}
                    </span>
                  </Tooltip>
                  <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                    <SummaryItem
                      className="border-b-[1px] border-gray-100 pt-0"
                      title={<AppFormattedMessage id="STAKEHOLDER" />}
                      value={
                        <Tooltip content={item.stakeholder.fullName}>
                          {item.stakeholder.fullName}
                        </Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="FROM_SHARE_CLASS" />}
                      value={
                        <Tooltip content={item.shareClassFrom.name}>
                          {item.shareClassFrom.name}
                        </Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="TO_SHARE_CLASS" />}
                      value={
                        <Tooltip content={item.shareClassTo.name}>{item.shareClassTo.name}</Tooltip>
                      }
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="NO_OF_SHARES" />}
                      value={Number(item.shares).toLocaleString('en-US') || ''}
                    />
                    <SummaryItem
                      className="border-b-[1px] border-gray-100"
                      title={<AppFormattedMessage id="SHARE_VALUE" />}
                      value={Number(
                        item.shares * item.shareClassFrom.conversionRatio || 1,
                      ).toLocaleString('en-US')}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="CONVERSION_RATIO" />}
                      value={`${item.shareClassFrom.conversionRatio}x`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isRenderValuation && (
            <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
              <span className="px-4 text-sm font-[550] text-gray-700">Valuation</span>
              <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                <SummaryItem
                  className="border-b-[1px] border-gray-100 pt-0"
                  title={<AppFormattedMessage id="EVENT_DATE" />}
                  value={
                    stepOne.eventDetails?.valuation?.date
                      ? format(stepOne.eventDetails?.valuation?.date, 'dd/MM/yyyy')
                      : '-'
                  }
                />

                <SummaryItem
                  className="border-b-[1px] border-gray-100"
                  title={<AppFormattedMessage id="EVENT_NAME" />}
                  value={
                    <Tooltip content={stepOne.eventDetails?.valuation?.name}>
                      {stepOne.eventDetails?.valuation?.name}
                    </Tooltip>
                  }
                />
                <SummaryItem
                  className="border-b-[1px] border-gray-100"
                  title={<AppFormattedMessage id="SHARES_PRICE" />}
                  value={roundedSharePrice.toLocaleString('en-US')}
                />
                <SummaryItem
                  className="pb-0"
                  title={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
                  value={(stepOne?.eventDetails?.valuation?.sharePrice
                    ? stepOne.eventDetails.valuation.sharePrice * issuedShares
                    : 0
                  ).toLocaleString('en-US')}
                />
              </div>
            </div>
          )}

          <EventAggregateDocumentsPreview event={event} files={stepTwo?.files} />
          <GrantSummaryPlainFields>
            <GrantSummaryPlainRow
              label={<AppFormattedMessage id="ADDITIONAL_NOTES" />}
              value={
                <span className="max-w-[250px] break-words">{stepTwo?.additionalNotes || '-'}</span>
              }
            />
          </GrantSummaryPlainFields>
        </div>
      </div>

      <div className="flex h-9 w-full shrink-0 justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>

          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={clicked}
            isLoading={isLoading}
            onClick={handleClick}
            type="button"
          >
            {initialStep?.type === EventFormType.GRANT && isGrantWithSignature ? (
              <AppFormattedMessage id="ADD_AND_SEND_GRANT" />
            ) : (
              <>
                <AppFormattedMessage id="ADD" /> {EventTypeButtonTitle[initialStep?.type]}
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
